import React from 'react';

const NON_DIGITS_RE = /\D/g;

interface UseCodeLikeInputParams {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	errorNonce?: number;

	codeLength: number;
	shakeDurationMs: number;
	staggerDelayMs: number;
	glowDefault: string;
	glowError: string;

	forwardedRef?: React.ForwardedRef<HTMLInputElement>;
}

interface UseCodeLikeInputResult {
	digits: string;
	isErrorAnimating: boolean;
	setInputRef: (node: HTMLInputElement | null) => void;
	getBubbleRef: (idx: number) => (node: HTMLSpanElement | null) => void;
	handleMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
	handleBlur: () => void;
	handleNativeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function normalizeCode(value: string, codeLength: number) {
	return value.replace(NON_DIGITS_RE, '').slice(0, codeLength);
}

function isDigitKey(key: string) {
	return key.length === 1 && key >= '0' && key <= '9';
}

export function useCodeLikeInput({
	value,
	onChange,
	disabled,
	errorNonce,
	codeLength,
	shakeDurationMs,
	staggerDelayMs,
	glowDefault,
	glowError,
	forwardedRef,
}: UseCodeLikeInputParams): UseCodeLikeInputResult {
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const bubbleRefs = React.useRef<Array<HTMLSpanElement | null>>([]);
	const prevDigitsRef = React.useRef<string>('');
	const [isErrorAnimating, setIsErrorAnimating] = React.useState(false);
	const errorTimeoutRef = React.useRef<number | null>(null);

	const digits = normalizeCode(value, codeLength);

	const focusInput = React.useCallback(() => {
		inputRef.current?.focus();
	}, []);

	React.useEffect(() => {
		if (!errorNonce) return;

		if (errorTimeoutRef.current) window.clearTimeout(errorTimeoutRef.current);
		setIsErrorAnimating(true);
		errorTimeoutRef.current = window.setTimeout(() => {
			setIsErrorAnimating(false);
			errorTimeoutRef.current = null;
		}, shakeDurationMs);

		return () => {
			if (errorTimeoutRef.current) window.clearTimeout(errorTimeoutRef.current);
		};
	}, [errorNonce, shakeDurationMs]);

	const setInputRef = React.useCallback(
		(node: HTMLInputElement | null) => {
			inputRef.current = node;
			if (!forwardedRef) return;
			if (typeof forwardedRef === 'function') {
				forwardedRef(node);
				return;
			}
			// eslint-disable-next-line no-param-reassign
			(forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
		},
		[forwardedRef],
	);

	const getBubbleRef = React.useCallback(
		(idx: number) => (node: HTMLSpanElement | null) => {
			bubbleRefs.current[idx] = node;
		},
		[],
	);

	React.useEffect(() => {
		const prefersReducedMotion =
			typeof window !== 'undefined' &&
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		const prev = prevDigitsRef.current;
		if (prev === digits) return;

		const appearingIndices: number[] = [];
		for (let idx = 0; idx < codeLength; idx += 1) {
			const was = prev[idx] ?? '';
			const now = digits[idx] ?? '';
			if (was || !now) continue;
			appearingIndices.push(idx);
		}

		for (let order = 0; order < appearingIndices.length; order += 1) {
			const idx = appearingIndices[order]!;
			const el = bubbleRefs.current[idx];
			if (!el) continue;

			el.getAnimations().forEach((a) => a.cancel());
			if (prefersReducedMotion) continue;

			const glow = isErrorAnimating ? glowError : glowDefault;
			el.animate(
				[
					{transform: 'scale(0.6)', opacity: 0.2, boxShadow: '0 0 0 0 rgba(0,0,0,0)'},
					{transform: 'scale(1.08)', opacity: 1, boxShadow: `0 0 0 10px ${glow}`},
					{transform: 'scale(1)', opacity: 1, boxShadow: '0 0 0 0 rgba(0,0,0,0)'},
				],
				{
					duration: 320,
					delay: order * staggerDelayMs,
					easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
				},
			);
		}

		prevDigitsRef.current = digits;
	}, [codeLength, digits, glowDefault, glowError, isErrorAnimating, staggerDelayMs]);

	React.useEffect(() => {
		if (disabled) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.isComposing) return;
			if (e.metaKey || e.ctrlKey || e.altKey) return;
			// iOS не отменяет keydown у поля, и тот же символ приходит ещё и в onChange.
			if (e.target === inputRef.current) return;

			const key = e.key;
			const isDigit = isDigitKey(key);
			const isBackspace = key === 'Backspace';
			if (!isDigit && !isBackspace) return;

			e.preventDefault();
			focusInput();

			if (isDigit) {
				onChange((digits + key).slice(0, codeLength));
				return;
			}

			onChange(digits.slice(0, -1));
		};

		window.addEventListener('keydown', handleKeyDown, true);
		return () => window.removeEventListener('keydown', handleKeyDown, true);
	}, [codeLength, digits, disabled, focusInput, onChange]);

	const handleNativeChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange(normalizeCode(e.target.value, codeLength));
		},
		[codeLength, onChange],
	);

	const handleMouseDown = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.preventDefault();
			focusInput();
		},
		[focusInput],
	);

	const handleBlur = React.useCallback(() => {
		if (disabled) return;
		window.setTimeout(focusInput, 0);
	}, [disabled, focusInput]);

	return {
		digits,
		isErrorAnimating,
		setInputRef,
		getBubbleRef,
		handleMouseDown,
		handleBlur,
		handleNativeChange,
	};
}

